import { useContext, useEffect, useState } from 'react'

import { faRedo, faSignalStream, faSpinner } from '@fortawesome/pro-regular-svg-icons'
import { faArrowLeft, faArrowRight, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, LinearProgress, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { ConfirmationDialog } from '@scoir/modals'

import IntegrationConfigurationContext from '../IntegrationConfigurationProvider/Context'
import { CopyToClipboard } from './CopyToClipboard'

const useStyles = makeStyles((theme) => ({
  progress: {
    width: '100%',
  },
  nextButton: {
    marginTop: theme.spacing(2),
    width: 'fit-content',
    alignSelf: 'flex-end',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  keyButton: {
    alignSelf: 'flex-start',
  },
  confirmButton: {
    height: 'fit-content',
  },
}))

const ManualSetup = ({ backToOptions }: { backToOptions: () => void }) => {
  const classes = useStyles()
  const [step, setStep] = useState<1 | 2 | 3>(1)

  const getStepUI = () => {
    switch (step) {
      case 1:
        return <ServerLocation setStep={setStep} />
      case 2:
        return <Credentials setStep={setStep} />
      case 3:
        return <TestConnection backToOptions={backToOptions} />
    }
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gridGap={24}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Button
          variant="text"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
          onClick={step === 1 ? backToOptions : () => setStep((step - 1) as 1 | 2 | 3)}
        >
          {step === 1 ? 'Back to options' : 'Back'}
        </Button>
        <Typography>Step {step} of 3</Typography>
      </Box>
      <LinearProgress
        className={classes.progress}
        variant="determinate"
        value={(step * 100) / 3}
      />

      <Typography>
        Enter your SFTP credentials to connect to your server and automate secure file transfers.{' '}
        <a
          href="https://scoir.helpdocs.io/article/4fbbxi0lu3-configuring-document-delivery-via-sftp"
          rel="noopener noreferrer"
          target="_blank"
        >
          Need more help? Learn more.
        </a>
      </Typography>
      {getStepUI()}
    </Box>
  )
}

type StepProps = {
  setStep: React.Dispatch<React.SetStateAction<1 | 2 | 3>>
}
const ServerLocation = ({ setStep }: StepProps) => {
  const classes = useStyles()
  const [hostname, setHostname] = useState('')
  const [port, setPort] = useState<number | null>(null)
  const [path, setPath] = useState('')
  const { state, onSaveServerLocation } = useContext(IntegrationConfigurationContext)

  useEffect(() => {
    setHostname(state.SFTP?.Credentials?.Host || '')
    setPort(state.SFTP?.Credentials?.Port || null)
    setPath(state.SFTP?.Credentials?.Path || '')
  }, [state.SFTP?.Credentials])

  return (
    <form
      className={classes.formContainer}
      onSubmit={(e) => {
        e.preventDefault()
        onSaveServerLocation({ hostname, port: port || 0, path })
        setStep(2)
      }}
    >
      <Typography variant="h6">Server Location</Typography>
      <Typography
        variant="body2"
        color="textSecondary"
      >
        This address will be used to connect to your SFTP server.
      </Typography>
      <Box
        display="flex"
        gridGap={16}
      >
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          color="primary"
          label="Hostname"
          name="hostname"
          id="sftp-hostname"
          FormHelperTextProps={{ role: 'alert' }}
          required
          helperText="e.g. example.org"
          value={hostname}
          onChange={(e) => setHostname(e.target.value)}
        />
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          color="primary"
          required
          type="number"
          inputProps={{ min: '0' }}
          helperText="e.g. 21"
          FormHelperTextProps={{ role: 'alert' }}
          label="Port"
          name="port"
          id="sftp-port"
          value={port}
          onChange={(e) => setPort(Number(e.target.value))}
        />
      </Box>
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        color="primary"
        FormHelperTextProps={{ role: 'alert' }}
        helperText="/destination/folder"
        label="File Path (optional)"
        name="path"
        id="sftp-path"
        value={path}
        onChange={(e) => setPath(e.target.value)}
      />
      <Button
        variant="contained"
        color="primary"
        disabled={hostname.length === 0 || !port}
        type="submit"
        className={classes.nextButton}
        endIcon={<FontAwesomeIcon icon={faArrowRight} />}
      >
        Next: Server Credentials
      </Button>
    </form>
  )
}
const Credentials = ({ setStep }: StepProps) => {
  const PASSWORD_PLACEHOLDER = '********'
  const classes = useStyles()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [publicKey, setPublicKey] = useState('')
  const [keyOperation, setKeyOperation] = useState<'generate' | 'remove'>()
  const { state, onKeyGeneration, onKeyRemoval, onSaveServerCredentials } = useContext(IntegrationConfigurationContext)

  useEffect(() => {
    setUsername(state.SFTP?.Credentials?.Username || '')
    setPassword(state.SFTP?.Credentials?.HasStoredPassword ? PASSWORD_PLACEHOLDER : '')
    setPublicKey(state.SFTP?.Credentials?.PublicKey || '')
  }, [state.SFTP?.Credentials])

  useEffect(() => {
    setKeyOperation(undefined)
  }, [state.keySuccessful])

  return (
    <form
      className={classes.formContainer}
      onSubmit={(e) => {
        e.preventDefault()
        onSaveServerCredentials({ username, password: password === PASSWORD_PLACEHOLDER ? undefined : password })
        setStep(3)
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        mb={2}
      >
        <Box width="80%">
          <Typography variant="h6">Server Credentials</Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            These credentials will be used to gain access to your SFTP server for file delivery. Provide a password or
            generate a public key to access your SFTP server.
          </Typography>
        </Box>
        <Button
          color="primary"
          onClick={() => {
            setUsername('')
            setPassword('')
            setPublicKey('')
          }}
        >
          Clear Credentials
        </Button>
      </Box>
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        color="primary"
        name="username"
        label="Username"
        autoComplete="username"
        required
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        color="primary"
        type="password"
        name="password"
        label="Password"
        autoComplete="current-password"
        disabled={Boolean(!username || publicKey)}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      {!publicKey ? (
        <Button
          color="primary"
          className={classes.keyButton}
          onClick={() => setKeyOperation(publicKey ? 'remove' : 'generate')}
        >
          Don’t have a password? Generate fresh key
        </Button>
      ) : (
        <Box mt={2}>
          <CopyToClipboard
            label="Public Key"
            value={publicKey}
          />
          <Box
            display="flex"
            gridGap={16}
          >
            <Button
              color="primary"
              startIcon={<FontAwesomeIcon icon={faRedo} />}
              onClick={() => setKeyOperation('generate')}
            >
              Regenerate
            </Button>
            <Button
              color="primary"
              startIcon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => setKeyOperation('remove')}
            >
              Remove
            </Button>
          </Box>
        </Box>
      )}
      <Button
        variant="contained"
        color="primary"
        type="submit"
        disabled={username.length === 0 || (password.length === 0 && !publicKey)}
        className={classes.nextButton}
        endIcon={<FontAwesomeIcon icon={faArrowRight} />}
      >
        Next: Test Connection
      </Button>
      <ConfirmationDialog
        open={Boolean(keyOperation)}
        headerText={`${keyOperation === 'remove' ? 'Remove your' : 'Generate a New'} SFTP Key`}
        onClose={() => setKeyOperation(undefined)}
        onConfirm={() => (keyOperation === 'remove' ? onKeyRemoval() : onKeyGeneration())}
      >
        <Typography>
          {`${
            keyOperation === 'remove' ? 'Removing your' : 'Generating a new key'
          } SFTP key will update the SFTP integration status to "Inactive". Would you like to
            proceed?`}
        </Typography>
      </ConfirmationDialog>
    </form>
  )
}
const TestConnection = ({ backToOptions }: { backToOptions: () => void }) => {
  const classes = useStyles()
  const [testing, setTesting] = useState(false)
  const { onConfigurationChange, onTestSftpConnection, sftpTestSuccessful, state } = useContext(
    IntegrationConfigurationContext,
  )

  const [open, setOpen] = useState(false)
  const handleConfiguredDeliveryMethod = () => {
    // Call context to handle configuration switching
    onConfigurationChange({ type: 'sftp' })
    backToOptions()
  }

  useEffect(() => {
    if (sftpTestSuccessful !== undefined) {
      setTesting(false)
    }
  }, [sftpTestSuccessful])

  return (
    <Box width="100%">
      <Typography variant="h6">Test Connection</Typography>
      <Typography
        variant="body2"
        color="textSecondary"
      >
        Confirm that we are able to connect to your SFTP server.
      </Typography>
      <Box
        display="flex"
        gridGap={16}
        marginTop={2}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setTesting(true)
            onTestSftpConnection()
          }}
          startIcon={<FontAwesomeIcon icon={testing ? faSpinner : faSignalStream} />}
          disabled={testing}
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </Button>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
      >
        {sftpTestSuccessful !== undefined && !testing ? (
          <Alert severity={sftpTestSuccessful ? 'success' : 'error'}>
            {sftpTestSuccessful
              ? 'SFTP Connection Successful!'
              : 'Test Connection failed. Please try again or contact support.'}
          </Alert>
        ) : (
          // Alignment purposes
          <div />
        )}
        <Box
          display="flex"
          gridGap={16}
        >
          {sftpTestSuccessful && (
            <Button
              variant="text"
              color="primary"
              className={classes.confirmButton}
              disabled={testing}
              // Nothing to save, just return to options
              onClick={backToOptions}
            >
              Save & Close
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={!sftpTestSuccessful || testing}
            className={classes.confirmButton}
            onClick={() => {
              if (state.Slate?.Status === 'Live') {
                setOpen(true)
                return
              }
              handleConfiguredDeliveryMethod()
            }}
          >
            Use Configuration
          </Button>
        </Box>
      </Box>
      <ConfirmationDialog
        open={open}
        headerText="Use Configuration?"
        onClose={() => setOpen(false)}
        confirmationText="Update Configuration"
        onConfirm={handleConfiguredDeliveryMethod}
      >
        <Typography>
          Are you sure you want to switch your configuration to use Manual Setup? Changes will apply to both Document
          Delivery and Handraisers.
        </Typography>
      </ConfirmationDialog>
    </Box>
  )
}

export default ManualSetup
